/*
  Shopify size converter
  Version: 2026-06-10.back-cup-static-elements-v1

  Summary
  - Keeps <option value="..."> untouched.
  - Updates dropdown option labels, or fixed text elements via updateSizeText().
  - Uses UK sizes as source/default values.
  - Converts for UK, USA, EU, FR, IT and AU regions.
  - Treats EU as EU member countries except FR and IT, which use their own columns.
  - Handles Shopify localization objects, direct country codes, or direct region strings.
  - Handles status text after a spaced hyphen, e.g. "32G - only 1 left!".
  - Handles bra sizes, back-only sizes, cup-only sizes, knickers, and combined slash sizes.
  - Handles numerical standard knicker aliases, e.g. "8", "10", "8/10".
  - Handles numerical CK grouped aliases, e.g. "8/10", "12/14", plus individual values.
  - Uses CK knicker sizing for Scantilly, Curvy Kate, Flirtelle, Adella and Liaison.
  - Uses standard knicker sizing for every other vendor, including missing vendors.

  Typical Shopify usage
    updateDropdownSizes(document.querySelector('select[name="options[Size]"]'), {
      localization: { country: { iso_code: {{ localization.country.iso_code | json }} } },
      vendor: {{ product.vendor | json }},
      sizeType: 'auto', // auto, bra, back, cup, knickers
      convertedSizeColumn: 13,
      minimumGap: 2
    });

  Fixed element usage
    updateSizeText('.new_size', {
      localization: { country: { iso_code: {{ localization.country.iso_code | json }} } },
      vendor: {{ product.vendor | json }},
      sizeType: 'auto'
    });
*/
(function () {
  'use strict';

  var VERSION = '2026-06-10.back-cup-static-elements-v1';

  var BRA_BACK_SIZES = {
    '28': { UK: '28', USA: '28', EU: '60', FR: '75', IT: '-', AU: '6' },
    '30': { UK: '30', USA: '30', EU: '65', FR: '80', IT: '-', AU: '8' },
    '32': { UK: '32', USA: '32', EU: '70', FR: '85', IT: '1', AU: '10' },
    '34': { UK: '34', USA: '34', EU: '75', FR: '90', IT: '2', AU: '12' },
    '36': { UK: '36', USA: '36', EU: '80', FR: '95', IT: '3', AU: '14' },
    '38': { UK: '38', USA: '38', EU: '85', FR: '100', IT: '4', AU: '16' },
    '40': { UK: '40', USA: '40', EU: '90', FR: '105', IT: '5', AU: '18' },
    '42': { UK: '42', USA: '42', EU: '95', FR: '110', IT: '6', AU: '20' },
    '44': { UK: '44', USA: '44', EU: '100', FR: '115', IT: '7', AU: '22' },
    '46': { UK: '46', USA: '46', EU: '105', FR: '120', IT: '8', AU: '24' },
    '48': { UK: '48', USA: '48', EU: '110', FR: '125', IT: '9', AU: '26' }
  };

  var BRA_CUP_SIZES = {
    A: { UK: 'A', USA: 'A', EU: 'A', FR: 'A', IT: 'A', AU: 'A' },
    B: { UK: 'B', USA: 'B', EU: 'B', FR: 'B', IT: 'B', AU: 'B' },
    C: { UK: 'C', USA: 'C', EU: 'C', FR: 'C', IT: 'C', AU: 'C' },
    D: { UK: 'D', USA: 'D', EU: 'D', FR: 'D', IT: 'D', AU: 'D' },
    DD: { UK: 'DD', USA: 'DD/E', EU: 'E', FR: 'E', IT: 'DD', AU: 'DD' },
    E: { UK: 'E', USA: 'DDD/F', EU: 'F', FR: 'F', IT: 'E', AU: 'E' },
    F: { UK: 'F', USA: 'G', EU: 'G', FR: 'G', IT: 'F', AU: 'F' },
    FF: { UK: 'FF', USA: 'H', EU: 'H', FR: 'H', IT: 'FF', AU: 'FF' },
    G: { UK: 'G', USA: 'I', EU: 'I', FR: 'I', IT: 'G', AU: 'G' },
    GG: { UK: 'GG', USA: 'J', EU: 'J', FR: 'J', IT: 'GG', AU: 'GG' },
    H: { UK: 'H', USA: 'K', EU: 'K', FR: 'K', IT: 'H', AU: 'H' },
    HH: { UK: 'HH', USA: 'L', EU: 'L', FR: 'L', IT: 'HH', AU: 'HH' },
    J: { UK: 'J', USA: 'M', EU: 'M', FR: 'M', IT: 'J', AU: 'J' },
    JJ: { UK: 'JJ', USA: 'N', EU: 'N', FR: 'N', IT: 'JJ', AU: 'JJ' },
    K: { UK: 'K', USA: 'O', EU: 'O', FR: 'O', IT: 'K', AU: 'K' },
    // Inferred fallback for combined product sizes such as 32K/KK.
    KK: { UK: 'KK', USA: 'P', EU: 'P', FR: 'P', IT: 'KK', AU: 'KK' }
  };

  var STANDARD_KNICKERS = [
    { SIZE: 'XS', UK: '8', USA: '4', EU: '34', FR: '8', IT: '8', AU: '8' },
    { SIZE: 'S', UK: '10', USA: '6', EU: '36', FR: '10', IT: '10', AU: '10' },
    { SIZE: 'M', UK: '12', USA: '8', EU: '38', FR: '12', IT: '12', AU: '12' },
    { SIZE: 'L', UK: '14', USA: '10', EU: '40', FR: '14', IT: '14', AU: '14' },
    { SIZE: 'XL', UK: '16', USA: '12', EU: '42', FR: '16', IT: '16', AU: '16' },
    { SIZE: '2XL', UK: '18', USA: '14', EU: '44', FR: '18', IT: '18', AU: '18' },
    { SIZE: '3XL', UK: '20', USA: '16', EU: '46', FR: '20', IT: '20', AU: '20' },
    { SIZE: '4XL', UK: '22', USA: '18', EU: '48', FR: '22', IT: '22', AU: '22' },
    { SIZE: '5XL', UK: '24', USA: '20', EU: '50', FR: '24', IT: '24', AU: '24' }
  ];

  var CK_KNICKERS = [
    { SIZE: 'S', UK: '8/10', USA: '4/6', EU: '34/36', FR: '36/38', IT: '36/38', AU: '8/10' },
    { SIZE: 'M', UK: '12/14', USA: '8/10', EU: '38/40', FR: '40/42', IT: '40/42', AU: '12/14' },
    { SIZE: 'L', UK: '16/18', USA: '12/14', EU: '42/44', FR: '44/46', IT: '44/46', AU: '16/18' },
    { SIZE: 'XL', UK: '20/22', USA: '16/18', EU: '46/48', FR: '48/50', IT: '48/50', AU: '20/22' },
    { SIZE: '2XL', UK: '24/26', USA: '20/22', EU: '50/52', FR: '52/54', IT: '52/54', AU: '24/26' },
    { SIZE: '3XL', UK: '28/30', USA: '24/26', EU: '54/56', FR: '56/58', IT: '56/58', AU: '28/30' }
  ];

  var SUPPORTED_REGIONS = { UK: true, USA: true, EU: true, FR: true, IT: true, AU: true };

  var EU_COUNTRIES_EX_FR_IT = {
    AT: true, BE: true, BG: true, HR: true, CY: true, CZ: true, DK: true,
    EE: true, FI: true, DE: true, GR: true, HU: true, IE: true, LV: true,
    LT: true, LU: true, MT: true, NL: true, PL: true, PT: true, RO: true,
    SK: true, SI: true, ES: true, SE: true
  };

  var CK_VENDOR_LOOKUP = {
    SCANTILLY: true,
    'CURVY KATE': true,
    FLIRTELLE: true,
    ADELLA: true,
    LIAISON: true
  };

  function normaliseText(value) {
    return String(value == null ? '' : value).replace(/\u00A0/g, ' ').trim();
  }

  function upper(value) {
    return normaliseText(value).toUpperCase();
  }

  function isUsableConvertedValue(value) {
    value = normaliseText(value);
    return !!value && value !== '-';
  }

  function getCountryCode(localizationOrCode) {
    var country;

    if (!localizationOrCode) return '';

    if (typeof localizationOrCode === 'string') {
      return upper(localizationOrCode);
    }

    country = localizationOrCode.country || localizationOrCode.current_country || localizationOrCode.currentCountry;

    if (typeof country === 'string') {
      return upper(country);
    }

    if (country && typeof country === 'object') {
      return upper(country.iso_code || country.isoCode || country.code || country.country_code || country.countryCode);
    }

    return upper(
      localizationOrCode.iso_code ||
      localizationOrCode.isoCode ||
      localizationOrCode.country_code ||
      localizationOrCode.countryCode ||
      localizationOrCode.region
    );
  }

  function getRegion(localizationOrCode) {
    var code = getCountryCode(localizationOrCode);

    if (code === 'GB' || code === 'UK') return 'UK';
    if (code === 'US' || code === 'USA') return 'USA';
    if (code === 'FR') return 'FR';
    if (code === 'IT') return 'IT';
    if (code === 'AU' || code === 'AUS') return 'AU';
    if (EU_COUNTRIES_EX_FR_IT[code]) return 'EU';
    if (SUPPORTED_REGIONS[code]) return code;

    return '';
  }

  function usesCkKnickers(vendor) {
    return !!CK_VENDOR_LOOKUP[upper(vendor)];
  }

  function stripExistingConversion(label) {
    return normaliseText(label).replace(/\s+\((UK|USA|EU|FR|IT|AU)\s+[^)]*\)/i, '').trim();
  }

  function splitStatusSuffix(label) {
    var clean = stripExistingConversion(label);
    var match = clean.match(/^(.*?)(\s+-\s+.+)$/);

    if (!match) {
      return { originalLabel: clean, size: clean, suffix: '' };
    }

    return {
      originalLabel: clean,
      size: normaliseText(match[1]),
      suffix: match[2]
    };
  }

  function getOptionBaseLabel(option) {
    var stored = option.getAttribute && option.getAttribute('data-original-size-label');
    var base;

    if (stored) return stored;

    base = stripExistingConversion(option.textContent || option.text || option.label || '');

    if (option.setAttribute && base) {
      option.setAttribute('data-original-size-label', base);
    }

    return base;
  }

  function setOptionLabel(option, label) {
    if ('textContent' in option) option.textContent = label;
    else option.text = label;

    if (option.hasAttribute && option.hasAttribute('label')) {
      option.label = label;
    }
  }

  function braLookupKey(label) {
    return upper(label).replace(/[\s-]/g, '');
  }

  function backLookupKey(label) {
    return upper(label).replace(/\b(UK|SIZE)\b/g, '').replace(/[^0-9]/g, '');
  }

  function cupLookupKey(label) {
    return upper(label).replace(/\b(CUP|SIZE)\b/g, '').replace(/[^A-Z/]/g, '');
  }

  function convertBackSize(sizeText, region) {
    var key;
    var row;
    var converted;

    if (region === 'UK') return null;

    key = backLookupKey(sizeText);
    row = BRA_BACK_SIZES[key];
    if (!row) return null;

    converted = normaliseText(row[region]);
    return isUsableConvertedValue(converted) ? converted : null;
  }

  function convertCupSize(sizeText, region, opts) {
    var key;
    var parts;
    var convertedParts = [];
    var i;

    if (region === 'UK') return null;

    key = cupLookupKey(sizeText);
    if (!key) return null;

    parts = key.split('/');

    for (i = 0; i < parts.length; i += 1) {
      var row = BRA_CUP_SIZES[parts[i]];
      var converted = row && normaliseText(row[region]);

      if (!isUsableConvertedValue(converted)) return null;
      convertedParts.push(converted);
    }

    return joinCombinedValues(convertedParts, opts);
  }

  function convertOneBraPart(ukBand, ukCup, region) {
    var bandRow;
    var cupRow;
    var convertedBand;
    var convertedCup;

    if (region === 'UK') return null;

    bandRow = BRA_BACK_SIZES[ukBand];
    cupRow = BRA_CUP_SIZES[ukCup];

    if (!bandRow || !cupRow) return null;

    convertedBand = normaliseText(bandRow[region]);
    convertedCup = normaliseText(cupRow[region]);

    if (!isUsableConvertedValue(convertedBand) || !isUsableConvertedValue(convertedCup)) return null;

    return convertedBand + convertedCup;
  }

  function convertBraSize(sizeText, region, opts) {
    var key = braLookupKey(sizeText);
    var match;
    var band;
    var cups;
    var convertedCups = [];
    var convertedBand;
    var i;

    if (region === 'UK') return null;

    match = key.match(/^(\d{2})([A-Z]+(?:\/[A-Z]+)*)$/);
    if (!match) return null;

    band = match[1];
    cups = match[2].split('/');

    convertedBand = BRA_BACK_SIZES[band] && normaliseText(BRA_BACK_SIZES[band][region]);
    if (!isUsableConvertedValue(convertedBand)) return null;

    for (i = 0; i < cups.length; i += 1) {
      var cupRow = BRA_CUP_SIZES[cups[i]];
      var convertedCup = cupRow && normaliseText(cupRow[region]);

      if (!isUsableConvertedValue(convertedCup)) return null;
      convertedCups.push(convertedCup);
    }

    return convertedBand + joinCombinedValues(convertedCups, opts);
  }

  function knickerLookupKey(label) {
    return upper(label)
      .replace(/\b(UK|SIZE)\b/g, '')
      .replace(/\s*\/\s*/g, '/')
      .replace(/\s+/g, '')
      .trim();
  }

  function splitRange(value) {
    return normaliseText(value).split('/').map(function (part) {
      return normaliseText(part);
    });
  }

  function getRowKeys(row) {
    var keys = [knickerLookupKey(row.SIZE), knickerLookupKey(row.UK)];
    var ukParts = splitRange(row.UK);
    var i;

    for (i = 0; i < ukParts.length; i += 1) {
      keys.push(knickerLookupKey(ukParts[i]));
    }

    return keys;
  }

  function findKnickerRow(baseLabel, rows) {
    var key = knickerLookupKey(baseLabel);
    var i;

    for (i = 0; i < rows.length; i += 1) {
      var keys = getRowKeys(rows[i]);
      var j;

      for (j = 0; j < keys.length; j += 1) {
        if (keys[j] === key) return rows[i];
      }
    }

    return null;
  }

  function convertCkIndividualNumber(sizeText, region) {
    var key = knickerLookupKey(sizeText);
    var i;

    if (!/^\d+$/.test(key)) return null;

    for (i = 0; i < CK_KNICKERS.length; i += 1) {
      var ukParts = splitRange(CK_KNICKERS[i].UK);
      var regionParts = splitRange(CK_KNICKERS[i][region]);
      var j;

      for (j = 0; j < ukParts.length; j += 1) {
        if (knickerLookupKey(ukParts[j]) === key && isUsableConvertedValue(regionParts[j])) {
          return regionParts[j];
        }
      }
    }

    return null;
  }

  function convertSingleKnickerSize(sizeText, region, rows, isCk) {
    var row;
    var converted;

    if (isCk) {
      converted = convertCkIndividualNumber(sizeText, region);
      if (converted) return converted;
    }

    row = findKnickerRow(sizeText, rows);
    if (!row) return null;

    converted = normaliseText(row[region]);
    return isUsableConvertedValue(converted) ? converted : null;
  }

  function joinCombinedValues(values, opts) {
    var joiner;
    var i;

    if (values.length <= 1) return values[0] || '';

    if (opts && opts.combinedSizeJoiner != null) {
      joiner = String(opts.combinedSizeJoiner);
    } else {
      joiner = '/';
      for (i = 0; i < values.length; i += 1) {
        if (String(values[i]).indexOf('/') !== -1) joiner = ' / ';
      }
    }

    return values.join(joiner);
  }

  function convertKnickerSize(sizeText, region, vendor, opts) {
    var isCk = usesCkKnickers(vendor);
    var rows = isCk ? CK_KNICKERS : STANDARD_KNICKERS;
    var directConverted = convertSingleKnickerSize(sizeText, region, rows, isCk);
    var key;
    var parts;
    var convertedParts = [];
    var i;

    key = knickerLookupKey(sizeText);

    if (directConverted) return directConverted;
    if (key.indexOf('/') === -1) return null;

    parts = key.split('/');

    for (i = 0; i < parts.length; i += 1) {
      var convertedPart = convertSingleKnickerSize(parts[i], region, rows, isCk);
      if (!convertedPart) return null;
      convertedParts.push(convertedPart);
    }

    return joinCombinedValues(convertedParts, opts);
  }

  function comparableSize(value) {
    return upper(value).replace(/\s+/g, '');
  }

  function repeatString(value, count) {
    var output = '';
    var i;

    for (i = 0; i < count; i += 1) output += value;
    return output;
  }

  function getLabelPadding(sizePart, opts) {
    var column = parseInt(opts.convertedSizeColumn == null ? opts.alignColumn : opts.convertedSizeColumn, 10);
    var minimumGap = parseInt(opts.minimumGap == null ? 2 : opts.minimumGap, 10);
    var padChar = opts.paddingCharacter == null ? '\u00A0' : String(opts.paddingCharacter);
    var baseLength = normaliseText(sizePart).length;

    if (opts.spaceBeforeBracket != null) return String(opts.spaceBeforeBracket);

    if (!column || column < 1) column = 13;
    if (!minimumGap || minimumGap < 1) minimumGap = 2;
    if (!padChar) padChar = ' ';

    return repeatString(padChar, Math.max(minimumGap, column - baseLength));
  }

  function formatConvertedLabel(labelParts, region, convertedSize, opts) {
    var labelMode = upper(opts && opts.labelMode ? opts.labelMode : '');

    // Straight swap mode: show only the converted size, while keeping any trailing status text.
    // Example: "34G - only 1 left!" -> "90I - only 1 left!" for FR.
    if (labelMode === 'CONVERTED' || labelMode === 'SWAP' || labelMode === 'REPLACE') {
      return convertedSize + labelParts.suffix;
    }

    // Default mode: keep original UK size and append the converted regional size.
    return labelParts.size + getLabelPadding(labelParts.size, opts) + '(' + region + ' ' + convertedSize + ')' + labelParts.suffix;
  }

  function resolveTarget(targetElm) {
    if (typeof targetElm === 'string') {
      return document.querySelector(targetElm);
    }
    return targetElm;
  }

  function resolveTextTargets(targetElm) {
    var target;

    if (typeof targetElm === 'string') {
      return Array.prototype.slice.call(document.querySelectorAll(targetElm));
    }

    target = targetElm;
    if (!target) return [];

    if (target.nodeType === 1) return [target];
    if (target.length != null) return Array.prototype.slice.call(target);

    return [];
  }

  function getRegionFromConfig(opts) {
    return opts.region ? getRegion(opts.region) : getRegion(opts.localization || opts.country || opts.countryCode);
  }

  function resolveConversion(sizeText, opts) {
    var region = getRegionFromConfig(opts);
    var sizeType = upper(opts.sizeType || 'auto');
    var vendor = opts.vendor || '';
    var convertedSize = null;
    var matchedType = '';

    if (!SUPPORTED_REGIONS[region]) {
      return { ok: false, reason: 'Unsupported region or country.', region: region || '' };
    }

    if (region === 'UK') {
      return { ok: false, reason: 'UK region does not need conversion.', region: region };
    }

    if (sizeType === 'BACK') {
      convertedSize = convertBackSize(sizeText, region, opts);
      matchedType = convertedSize ? 'back' : '';
    } else if (sizeType === 'CUP') {
      convertedSize = convertCupSize(sizeText, region, opts);
      matchedType = convertedSize ? 'cup' : '';
    } else if (sizeType === 'BRA') {
      convertedSize = convertBraSize(sizeText, region, opts);
      matchedType = convertedSize ? 'bra' : '';
    } else if (sizeType === 'KNICKERS' || sizeType === 'KNICKER' || sizeType === 'BRIEF' || sizeType === 'BRIEFS') {
      convertedSize = convertKnickerSize(sizeText, region, vendor, opts);
      matchedType = convertedSize ? 'knickers' : '';
    } else {
      convertedSize = convertBraSize(sizeText, region, opts);
      if (convertedSize) matchedType = 'bra';

      if (!convertedSize) {
        convertedSize = convertBackSize(sizeText, region, opts);
        if (convertedSize) matchedType = 'back';
      }

      if (!convertedSize) {
        convertedSize = convertCupSize(sizeText, region, opts);
        if (convertedSize) matchedType = 'cup';
      }

      if (!convertedSize) {
        convertedSize = convertKnickerSize(sizeText, region, vendor, opts);
        if (convertedSize) matchedType = 'knickers';
      }
    }

    if (!convertedSize) {
      return { ok: false, reason: 'No conversion found.', region: region };
    }

    return {
      ok: true,
      region: region,
      convertedSize: convertedSize,
      matchedType: matchedType,
      knickerTable: usesCkKnickers(vendor) ? 'ck-knickers' : 'standard-knickers'
    };
  }

  function shouldShowConvertedLabel(labelParts, conversion, opts) {
    if (!conversion.ok) return false;
    if (opts.appendSameSize === true) return true;
    if (conversion.matchedType === 'knickers' && opts.appendSameSizeForKnickers !== false) return true;
    if (conversion.matchedType !== 'knickers' && opts.appendSameSizeForBras !== false) return true;
    return comparableSize(labelParts.size) !== comparableSize(conversion.convertedSize);
  }

  function getConvertedSizeLabel(originalLabel, config) {
    var opts = config || {};
    var labelParts = splitStatusSuffix(originalLabel);
    var conversion;

    if (!labelParts.size) {
      return { ok: false, label: labelParts.originalLabel, reason: 'Empty label.' };
    }

    conversion = resolveConversion(labelParts.size, opts);

    if (!shouldShowConvertedLabel(labelParts, conversion, opts)) {
      return {
        ok: false,
        label: labelParts.originalLabel,
        reason: conversion.reason || 'Converted size matches original size.',
        region: conversion.region || '',
        matchedType: conversion.matchedType || ''
      };
    }

    return {
      ok: true,
      label: formatConvertedLabel(labelParts, conversion.region, conversion.convertedSize, opts),
      originalLabel: labelParts.originalLabel,
      size: labelParts.size,
      suffix: labelParts.suffix,
      region: conversion.region,
      convertedSize: conversion.convertedSize,
      matchedType: conversion.matchedType,
      knickerTable: conversion.knickerTable
    };
  }

  /**
   * Convert a Shopify size dropdown option label to include regional size guidance.
   *
   * @param {HTMLSelectElement|string} targetElm select element or selector.
   * @param {Object} config
   * @param {Object|string} config.localization Shopify localization object, country code, or region.
   * @param {string} config.vendor Product vendor. Used to choose CK knicker conversion table.
   * @param {'auto'|'bra'|'back'|'cup'|'knickers'} config.sizeType Defaults to "auto".
   * @param {boolean} config.appendSameSize Force same-looking conversions to show.
   * @param {boolean} config.appendSameSizeForBras Defaults true outside the UK.
   * @param {boolean} config.appendSameSizeForKnickers Defaults true outside the UK.
   * @param {number} config.convertedSizeColumn Character column where the converted size starts. Defaults to 13.
   * @param {number} config.minimumGap Minimum number of spaces before the converted size. Defaults to 2.
   * @param {string} config.paddingCharacter Padding character. Defaults to non-breaking space.
   * @param {string} config.spaceBeforeBracket Optional fixed spacing string. Overrides column alignment.
   * @param {string} config.combinedSizeJoiner Optional joiner for slash-combined sizes.
   * @param {'append'|'converted'|'swap'|'replace'} config.labelMode Use 'converted' for straight-swap labels.
   * @returns {Object} Useful debug details.
   */
  function updateDropdownSizes(targetElm, config) {
    var select = resolveTarget(targetElm);
    var opts = config || {};
    var region = getRegionFromConfig(opts);
    var convertedCount = 0;
    var skippedCount = 0;
    var lastKnickerTable = usesCkKnickers(opts.vendor || '') ? 'ck-knickers' : 'standard-knickers';
    var i;

    if (typeof window !== 'undefined' && window.CKSizeConversionDisabled === true) {
      return { ok: false, reason: 'Size conversion disabled by global flag.' };
    }

    if (!select || !select.options) {
      return { ok: false, reason: 'Target is not a select element.', region: region || '' };
    }

    if (!SUPPORTED_REGIONS[region]) {
      return { ok: false, reason: 'Unsupported region or country.', region: region || '' };
    }

    for (i = 0; i < select.options.length; i += 1) {
      var option = select.options[i];
      var baseLabel = getOptionBaseLabel(option);
      var result;

      if (!baseLabel || option.disabled) {
        skippedCount += 1;
        setOptionLabel(option, baseLabel);
        continue;
      }

      result = getConvertedSizeLabel(baseLabel, opts);

      if (result.ok) {
        convertedCount += 1;
        lastKnickerTable = result.knickerTable || lastKnickerTable;
      } else {
        skippedCount += 1;
      }

      setOptionLabel(option, result.label || baseLabel);

      if (option.setAttribute) {
        option.setAttribute('data-size-region', region);
        option.setAttribute('data-original-size-core', splitStatusSuffix(baseLabel).size || '');
        option.setAttribute('data-original-size-suffix', splitStatusSuffix(baseLabel).suffix || '');
        if (result.matchedType) option.setAttribute('data-size-conversion-type', result.matchedType);
      }
    }

    return {
      ok: true,
      region: region,
      converted: convertedCount,
      skipped: skippedCount,
      knickerTable: lastKnickerTable
    };
  }

  /**
   * Convert fixed text elements such as <span class="new_size">34G</span>.
   * Accepts a selector, one element, NodeList, HTMLCollection or array of elements.
   */
  function updateSizeText(targetElm, config) {
    var opts = config || {};
    var region = getRegionFromConfig(opts);
    var elements = resolveTextTargets(targetElm);
    var convertedCount = 0;
    var skippedCount = 0;
    var i;

    if (typeof window !== 'undefined' && window.CKSizeConversionDisabled === true) {
      return { ok: false, reason: 'Size conversion disabled by global flag.' };
    }

    if (!elements.length) {
      return { ok: false, reason: 'No matching text elements found.', region: region || '' };
    }

    if (!SUPPORTED_REGIONS[region]) {
      return { ok: false, reason: 'Unsupported region or country.', region: region || '' };
    }

    for (i = 0; i < elements.length; i += 1) {
      var el = elements[i];
      var stored = el.getAttribute && el.getAttribute('data-original-size-label');
      var baseLabel = stored || stripExistingConversion(el.textContent || '');
      var result;

      if (el.setAttribute && baseLabel && !stored) {
        el.setAttribute('data-original-size-label', baseLabel);
      }

      result = getConvertedSizeLabel(baseLabel, opts);

      if (result.ok) {
        convertedCount += 1;
        el.textContent = result.label;
      } else {
        skippedCount += 1;
        el.textContent = result.label || baseLabel;
      }

      if (el.setAttribute) {
        el.setAttribute('data-size-region', region);
        if (result.matchedType) el.setAttribute('data-size-conversion-type', result.matchedType);
      }
    }

    return {
      ok: true,
      region: region,
      converted: convertedCount,
      skipped: skippedCount
    };
  }

  // Browser global exports for Shopify theme snippets.
  window.updateDropdownSizes = updateDropdownSizes;
  window.updateSizeText = updateSizeText;
  window.getConvertedSizeLabel = getConvertedSizeLabel;
  window.CKSizeConverter = {
    version: VERSION,
    updateDropdownSizes: updateDropdownSizes,
    updateSizeText: updateSizeText,
    getConvertedSizeLabel: getConvertedSizeLabel
  };

  // Backward-compatible alias matching the example name from the brief.
  window.updatedropdownus = updateDropdownSizes;
}());
